import { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  ProductGroupCard,
  formatDate,
  toArray,
  type ProductGroup,
  type ProductItem,
} from '../../components/patients/PatientProductsDisplay';

interface Props {
  data: Record<string, unknown> | null | undefined;
}

function normalizeItems(items: unknown): ProductItem[] {
  return toArray<ProductItem>(items).map((item) => ({
    ...item,
    quantity: item.quantity || 1,
  }));
}

function buildGroups(raw: Record<string, unknown>): ProductGroup[] {
  return Object.values(raw)
    .map((entry) => {
      const g = entry as ProductGroup;
      return {
        ...g,
        available: normalizeItems(g.available),
        upcoming: normalizeItems(g.upcoming),
        appointments: [],
        comped: [],
        purchase: [],
        unavailable: [],
        onHold: [],
        appointmentsQuantity: 0,
        compedQuantity: 0,
        usedQuantity: 0,
        expireQuantity: 0,
        deleteQuantity: 0,
        onHoldQuantity: 0,
      } as ProductGroup;
    })
    .filter(
      (g) => (g.availableQuantity ?? 0) > 0 || (g.upcomingQuantity ?? 0) > 0,
    );
}

interface ExpiringEntry {
  name: string;
  quantity: number;
  expireDate: string;
}

function getExpiringProducts(groups: ProductGroup[]): ExpiringEntry[] {
  const now = new Date();
  const twoWeeks = new Date(now);
  twoWeeks.setDate(twoWeeks.getDate() + 14);

  const entries: ExpiringEntry[] = [];

  for (const group of groups) {
    const available = toArray<ProductItem>(group.available);
    const byExpire = new Map<string, ExpiringEntry>();

    for (const item of available) {
      if (!item.expireDate || item.expireDate === '0000-00-00 00:00:00') continue;
      if (item.scheduledAppointmentsID) continue;

      const expires = new Date(item.expireDate);
      if (expires <= now || expires > twoWeeks) continue;

      const key = `${item.productTypeName || item.productName}|${item.expireDate}`;
      const existing = byExpire.get(key);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        byExpire.set(key, {
          name: item.productTypeName || item.productName,
          quantity: item.quantity,
          expireDate: item.expireDate,
        });
      }
    }

    entries.push(...byExpire.values());
  }

  entries.sort((a, b) => new Date(a.expireDate).getTime() - new Date(b.expireDate).getTime());
  return entries;
}

export default function PatientInventoryPanel({ data }: Props) {
  const groups = useMemo(() => (data ? buildGroups(data) : []), [data]);
  const expiring = useMemo(() => getExpiringProducts(groups), [groups]);

  if (groups.length === 0) {
    return (
      <div className="text-sm text-gray-400">
        No available products for this patient.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500">
        These are the patient's available products across all types, not the products required for this appointment.
      </div>

      {expiring.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5" />
            Expiring Soon
          </div>
          <div className="space-y-0.5">
            {expiring.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                <span className="font-medium">{e.quantity}x {e.name}</span>
                <span className="opacity-70">expires {formatDate(e.expireDate)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1">
        {groups.map((g) => (
          <ProductGroupCard
            key={`pt-${g.productTypeID}`}
            group={g}
            listType="product-types"
            onAction={async () => {}}
            readOnly
          />
        ))}
      </div>
    </div>
  );
}
